/**
 * Enrollment Error Utilities
 *
 * Helper functions for parsing and handling enrollment-related errors from the REST API.
 */

/**
 * Enrollment error types that can be determined from API errors
 */
export type EnrollmentErrorType = 'unauthorized' | 'not_enrolled' | 'unknown';

/**
 * Result of parsing an enrollment error
 */
export interface ParsedEnrollmentError {
	type: EnrollmentErrorType;
	message: string;
}

/**
 * Error codes that indicate user is not authenticated
 */
const UNAUTHORIZED_CODES = new Set([
	'rest_forbidden',
	'leat_rest_invalid_user',
]);

/**
 * Error codes that indicate user is logged in but not enrolled
 */
const NOT_ENROLLED_CODES = new Set([
	'no_contact_uuid',
	'rest_post_invalid_id',
	'spend-rules-claim', // Spend rules API returns this when user is not enrolled
]);

/**
 * Parse an API error to determine enrollment status
 *
 * @param error - The error from the API call
 * @returns Parsed error information
 *
 * @example
 * ```ts
 * try {
 *   await apiFetch({ path: '/leat/v1/contact' });
 * } catch (error) {
 *   const parsed = parseEnrollmentError(error);
 *   if (parsed.type === 'unauthorized') {
 *     setEnrollmentStatus('guest');
 *   } else if (parsed.type === 'not_enrolled') {
 *     setEnrollmentStatus('loggedInNotEnrolled');
 *   }
 * }
 * ```
 */
export function parseEnrollmentError(error: unknown): ParsedEnrollmentError {
	const errorMessage = getErrorMessage(error);
	const errorCode = getErrorCode(error);

	// Check for unauthorized/forbidden errors (user not logged in)
	if (isUnauthorizedError(errorMessage, errorCode)) {
		return {
			type: 'unauthorized',
			message: errorMessage || 'User is not authenticated',
		};
	}

	// Check for not-enrolled errors (user logged in but no contact)
	if (isNotEnrolledError(errorMessage, errorCode, error)) {
		return {
			type: 'not_enrolled',
			message: errorMessage || 'User is not enrolled in the loyalty program',
		};
	}

	// Unknown error
	return {
		type: 'unknown',
		message: errorMessage || 'An unexpected error occurred',
	};
}

/**
 * Check if an error indicates the user is not authenticated
 */
function isUnauthorizedError(message: string, code: string): boolean {
	const messageLower = message.toLowerCase();

	return (
		UNAUTHORIZED_CODES.has(code) ||
		messageLower.includes('401') ||
		messageLower.includes('403') ||
		messageLower.includes('unauthorized') ||
		messageLower.includes('forbidden')
	);
}

/**
 * Check if an error indicates the user is logged in but not enrolled
 */
function isNotEnrolledError(message: string, code: string, error?: unknown): boolean {
	const messageLower = message.toLowerCase();

	// Check if error is a Response object with 404 status
	if (error && typeof error === 'object' && 'status' in error) {
		const response = error as Response;
		if (response.status === 404) {
			return true;
		}
	}

	return (
		NOT_ENROLLED_CODES.has(code) ||
		messageLower.includes('404') ||
		messageLower.includes('not found')
	);
}

/**
 * Extract error message from various error formats
 */
function getErrorMessage(error: unknown): string {
	if (!error) return '';

	// Check for WP REST API error format
	if (typeof error === 'object' && error !== null) {
		const err = error as Record<string, unknown>;

		// Try message property first
		if (typeof err.message === 'string') {
			return err.message;
		}

		// Try data.message for WP REST errors
		if (
			typeof err.data === 'object' &&
			err.data !== null &&
			typeof (err.data as Record<string, unknown>).message === 'string'
		) {
			return (err.data as Record<string, unknown>).message as string;
		}
	}

	// Fallback to string conversion
	if (error instanceof Error) {
		return error.message;
	}

	return String(error);
}

/**
 * Extract error code from various error formats
 */
function getErrorCode(error: unknown): string {
	if (!error || typeof error !== 'object') return '';

	const err = error as Record<string, unknown>;

	// Try code property
	if (typeof err.code === 'string') {
		return err.code;
	}

	// Try data.code for WP REST errors
	if (
		typeof err.data === 'object' &&
		err.data !== null &&
		typeof (err.data as Record<string, unknown>).code === 'string'
	) {
		return (err.data as Record<string, unknown>).code as string;
	}

	return '';
}
